import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import excelIcon from "../../../src/assets/images/excel.svg"

import axios from "axios"
import apiURl from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import * as XLSX from "xlsx" // Add this: npm install xlsx
import { filter } from "lodash"

const ZonesForCarrier = () => {
  const { carrierId } = useParams()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [zones, setZones] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedZoneId, setSelectedZoneId] = useState(null)
  const [uploadData, setUploadData] = useState([]) // Parsed Excel data for preview
  const [uploadErrors, setUploadErrors] = useState([]) // Errors/duplicates for preview
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [addFormData, setAddFormData] = useState({
    country_name: "",
    country_code: "",
    value: "",
  })

  // Add these new states after existing useState declarations
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState({
    country_name: "",
    country_code: "",
    value: "",
  })
  const [editingZoneId, setEditingZoneId] = useState(null)

  //Carriers
  const [carrierName, setCarrierName] = useState("")
  const fetchCarriers = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/list`)
      if (data.status === "success") {
        const matchedCarrier = data.carriers.filter(
          carrier => carrier.id === Number(carrierId)
        )[0]
        if (matchedCarrier) {
          setCarrierName(matchedCarrier.name) // Assuming you have setCarrierName state setter
        } else {
          console.log("No carrier found with ID:", carrierId)
        }
      } else {
        toastr.error(data.message || "Failed to fetch carriers")
      }
    } catch (err) {
      toastr.error("Error fetching carriers")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCarriers()
  }, [])

  const downloadSampleExcel = () => {
    const sampleData = [
      ["Country Name", "Country Code", "Value"], // Headers
      ["Bahrain", "BH", 1],
      ["Jordan", "JO", 1],
      ["Kuwait", "KW", 1],
      ["Oman", "OM", 1],
      ["Qatar", "QA", 1],
      ["Turkey", "TR", 1],
      ["United Kingdom", "GB", 2],
    ]

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(sampleData)
    XLSX.utils.book_append_sheet(wb, ws, "Zones")
    XLSX.writeFile(wb, `sample-zones-${carrierId}.xlsx`)
  }

  //Original Data
  const handleDownload = () => {
    const data = [
      ["Country Name", "Country Code", "Value"],
      ...zones.map(zone => [zone.country_name, zone.country_code, zone.value]),
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Zones")
    XLSX.writeFile(wb, `${carrierName}_zones_export.xlsx`)
  }

  // Fetch zones for this carrier
  const fetchZones = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/${carrierId}/zones`)
      if (data.status === "success") {
        setZones(data.data)
      } else {
        setError(data.message || "Failed to fetch zones")
      }
    } catch (err) {
      console.error("Error fetching zones:", err)
      setError("Error fetching zones")
    } finally {
      setIsLoading(false)
      setLoading(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "country_name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Code",
        accessor: "country_code",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Value",
        accessor: "value",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: ({ row, value }) => (
          <div className="d-flex gap-2">
            <Button
              color="warning"
              size="sm"
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setSelectedZoneId(row.original.id)
                setShowDeleteModal(true)
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  const handleDelete = () => {
    if (selectedZoneId) {
      removeZone(selectedZoneId)
    }
    setShowDeleteModal(false)
  }

  useEffect(() => {
    fetchZones()
  }, [carrierId])

  // Handle add form input changes
  const handleAddInputChange = e => {
    const { name, value } = e.target
    setAddFormData(prev => ({ ...prev, [name]: value }))
  }

  // Add new zone (single)
  const addZone = async e => {
    e.preventDefault()
    if (
      !addFormData.country_name ||
      !addFormData.country_code ||
      !addFormData.value
    ) {
      toastr.error("Please fill all fields")
      return
    }
    try {
      const { data } = await axios.post(
        `${apiURl}/carriers/${carrierId}/zones`,
        {
          country_name: addFormData.country_name,
          country_code: addFormData.country_code,
          value: parseInt(addFormData.value),
        }
      )
      if (data.status === "success") {
        toastr.success("Zone added successfully")
        setAddFormData({ country_name: "", country_code: "", value: "" }) // Reset form
        setShowAddModal(false)
        fetchZones() // Refresh list
      } else {
        // This block only hit if backend returns 2xx with status: "error" (unlikely, but handle)
        toastr.error(data.message || "Failed to add zone")
      }
    } catch (err) {
      console.error("Error adding zone:", err)
      const errorMessage =
        err.response?.data?.message || err.message || "Error adding zone"
      if (errorMessage.includes("already exists")) {
        toastr.info(errorMessage)
      } else {
        toastr.error(errorMessage)
      }
    }
  }

  // Remove zone
  const removeZone = async zoneId => {
    try {
      const { data } = await axios.delete(
        `${apiURl}/carriers/${carrierId}/zones/${zoneId}`
      )
      if (data.status === "success") {
        toastr.success("Zone removed successfully")
        fetchZones() // Refresh list
      } else {
        toastr.error(data.message || "Failed to remove zone")
      }
    } catch (err) {
      console.error("Error removing zone:", err)
      toastr.error("Error removing zone")
    }
  }

  // Open Edit (placeholder - implement if needed)
  // Replace the openEdit function
  const openEdit = zone => {
    setEditFormData({
      country_name: zone.country_name,
      country_code: zone.country_code,
      value: zone.value.toString(),
    })
    setEditingZoneId(zone.id)
    setShowEditModal(true)
  }

  // Add this new function for handling edit input changes (after handleAddInputChange)
  const handleEditInputChange = e => {
    const { name, value } = e.target
    setEditFormData(prev => ({ ...prev, [name]: value }))
  }

  // Add this new function for editing a zone (after addZone)
  const editZone = async e => {
    e.preventDefault()
    if (
      !editFormData.country_name ||
      !editFormData.country_code ||
      !editFormData.value
    ) {
      toastr.error("Please fill all fields")
      return
    }
    try {
      const { data } = await axios.put(
        `${apiURl}/carriers/${carrierId}/zones/${editingZoneId}`,
        {
          country_name: editFormData.country_name,
          country_code: editFormData.country_code,
          value: parseInt(editFormData.value),
        }
      )
      if (data.status === "success") {
        toastr.success("Zone updated successfully")
        setEditFormData({ country_name: "", country_code: "", value: "" }) // Reset form
        setShowEditModal(false)
        fetchZones() // Refresh list
      } else {
        toastr.error(data.message || "Failed to update zone")
      }
    } catch (err) {
      console.error("Error updating zone:", err)
      const errorMessage =
        err.response?.data?.message || err.message || "Error updating zone"
      if (errorMessage.includes("already exists")) {
        toastr.info(errorMessage)
      } else {
        toastr.error(errorMessage)
      }
    }
  }

  // Handle Excel file upload
  // Handle Excel file upload
  const handleFileUpload = e => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = event => {
      const data = new Uint8Array(event.target.result)
      const workbook = XLSX.read(data, { type: "array" })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })

      if (jsonData.length === 0) {
        toastr.error("No data found in the file")
        return
      }

      // Validate headers
      const headers = jsonData[0].map(h => h?.toString().trim())
      const expectedHeaders = ["Country Name", "Country Code", "Value"]
      if (
        headers.length !== expectedHeaders.length ||
        !headers.every((h, i) => h === expectedHeaders[i])
      ) {
        toastr.error(
          `The format is not matched. Expected headers: ${expectedHeaders.join(
            ", "
          )}`
        )
        return
      }

      const rows = jsonData.slice(1)

      // Map to expected format (adjust column indices if needed: 0=Country Name, 1=Country Code, 2=Value)
      const parsedData = rows
        .map(row => ({
          country_name: row[0] || "",
          country_code: row[1] || "",
          value: row[2] || "",
        }))
        .filter(row => row.country_name && row.country_code && row.value) // Skip empty rows

      // Check duplicates against existing zones (by country_code)
      const existingCodes = new Set(
        zones.map(z => z.country_code.toUpperCase())
      )
      const errors = []
      const validData = parsedData.filter((row, index) => {
        const code = row.country_code.toUpperCase()
        if (existingCodes.has(code)) {
          errors.push({
            index: index + 2,
            message: `Duplicate country code: ${code} (already exists)`,
          }) // Row num starts from 2 (header +1)
          return false
        }
        if (!/^[A-Z]{2}$/.test(row.country_code)) {
          // Basic validation for code (2 uppercase letters)
          errors.push({
            index: index + 2,
            message: `Invalid country code format: ${row.country_code}`,
          })
          return false
        }
        if (isNaN(row.value) || parseInt(row.value) < 0) {
          errors.push({
            index: index + 2,
            message: `Invalid value: ${row.value} (must be non-negative number)`,
          })
          return false
        }
        return true
      })

      setUploadData(validData)
      setUploadErrors(errors)

      if (validData.length > 0 || errors.length > 0) {
        setShowUploadModal(true)
      } else {
        toastr.error("No valid data found in the file")
      }
      e.target.value = ""
    }
    reader.readAsArrayBuffer(file)
  }

  // Confirm and upload bulk
  const confirmUpload = async () => {
    if (uploadData.length === 0) {
      toastr.error("No data to upload")
      return
    }
    try {
      const { data } = await axios.post(
        `${apiURl}/carriers/${carrierId}/zones/bulk`,
        {
          zones: uploadData.map(d => ({
            country_name: d.country_name,
            country_code: d.country_code.toUpperCase(),
            value: parseInt(d.value),
          })),
        }
      )
      if (data.status === "success") {
        toastr.success(
          `Successfully uploaded ${
            data.insertedCount || uploadData.length
          } zones`
        )
        setShowUploadModal(false)
        setUploadData([])
        setUploadErrors([])
        fetchZones()
      } else {
        toastr.error(data.message || "Failed to upload zones")
      }
    } catch (err) {
      console.error("Error uploading zones:", err)
      toastr.error("Error uploading zones")
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="card-title mb-0">
              {carrierName ? (
                <>
                  Zones For
                  <span className="text-primary">
                    <Badge
                      pill
                      className="badge-soft-primary ms-1 font-size-16"
                    >
                      {carrierName}
                    </Badge>
                  </span>{" "}
                  Carrier
                </>
              ) : (
                "Zone"
              )}
            </h5>
          </Col>
          <Col className="text-end">
            <div className="d-flex gap-2 align-items-center float-end">
              <Button
                color="success"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="bx bx-upload"></i> Upload Excel
              </Button>
              <Button
                color="info"
                className="btn btn-sm"
                onClick={downloadSampleExcel}
              >
                <i className="bx bx-download"></i> Download Sample (.xlsx)
              </Button>
              <Button
                color="primary"
                onClick={() => setShowAddModal(true)}
                className="btn btn-sm"
              >
                <i className="bx bx-plus"></i> Add Single Zone
              </Button>

              <Link
                to="/int-carriers"
                className="btn btn-outline-secondary btn-sm"
              >
                ← Back
              </Link>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </Col>
        </Row>

        {/* Existing Zones Table */}
        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="float-end mt-3">
                  <img
                    src={excelIcon}
                    alt="excel.svg"
                    onClick={handleDownload}
                    disabled={zones.length === 0}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #bfc1c5",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                    id="excelIcon"
                  />
                  <UncontrolledTooltip placement="left" target="excelIcon">
                    Download Data
                  </UncontrolledTooltip>
                </div>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4">
                    <Spinner
                      color="primary"
                      style={{ width: "3rem", height: "3rem" }}
                      className="mb-2"
                    />
                    <small className="text-muted">
                      Please wait—this may take a moment for large files.
                    </small>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={zones}
                    customPageSize={10}
                    isGlobalFilter={true}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Single Zone Modal */}
      <Modal
        isOpen={showAddModal}
        toggle={() => {
          setShowAddModal(false)
          setAddFormData({ country_name: "", country_code: "", value: "" })
        }}
        centered
      >
        <ModalHeader toggle={() => setShowAddModal(false)}>
          Add New Zone
        </ModalHeader>
        <Form onSubmit={addZone}>
          <ModalBody>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Country Name *</Label>
                  <Input
                    type="text"
                    name="country_name"
                    value={addFormData.country_name}
                    onChange={handleAddInputChange}
                    placeholder="e.g., Bahrain"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Country Code *</Label>
                  <Input
                    type="text"
                    name="country_code"
                    value={addFormData.country_code}
                    onChange={handleAddInputChange}
                    placeholder="e.g., BH"
                    maxLength={10}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Value *</Label>
                  <Input
                    type="number"
                    name="value"
                    value={addFormData.value}
                    onChange={handleAddInputChange}
                    min="0"
                    placeholder="e.g., 1"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              className="btn btn-sm"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button color="primary" className="btn btn-sm" type="submit">
              Add Zone
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Upload Preview Modal */}
      <Modal
        isOpen={showUploadModal}
        toggle={() => {
          setShowUploadModal(false)
          setUploadData([])
          setUploadErrors([])
        }}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setShowUploadModal(false)}>
          Upload Preview
        </ModalHeader>
        <ModalBody>
          {uploadErrors.length > 0 && (
            <Alert color="warning">
              <strong>{uploadErrors.length} error(s) found:</strong>
              <ul>
                {uploadErrors.map((err, idx) => (
                  <li key={idx}>
                    {err.message} (Row {err.index})
                  </li>
                ))}
              </ul>
              <p>These rows will be skipped during upload.</p>
            </Alert>
          )}
          {uploadData.length > 0 ? (
            <Table size="sm">
              <thead>
                <tr>
                  <th>Country Name</th>
                  <th>Country Code</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {uploadData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.country_name}</td>
                    <td>{row.country_code}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No valid data to upload.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button
            color="success"
            className="btn btn-sm"
            onClick={confirmUpload}
            disabled={uploadData.length === 0}
          >
            Upload {uploadData.length} Zones
          </Button>
        </ModalFooter>
      </Modal>
      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        toggle={() => {
          setShowEditModal(false)
          setEditFormData({ country_name: "", country_code: "", value: "" })
        }}
        centered
      >
        <ModalHeader toggle={() => setShowEditModal(false)}>
          Edit Zone
        </ModalHeader>
        <Form onSubmit={editZone}>
          <ModalBody>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Country Name *</Label>
                  <Input
                    type="text"
                    name="country_name"
                    value={editFormData.country_name}
                    onChange={handleEditInputChange}
                    placeholder="e.g., Bahrain"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Country Code *</Label>
                  <Input
                    type="text"
                    name="country_code"
                    value={editFormData.country_code}
                    onChange={handleEditInputChange}
                    placeholder="e.g., BH"
                    maxLength={10}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Value *</Label>
                  <Input
                    type="number"
                    name="value"
                    value={editFormData.value}
                    onChange={handleEditInputChange}
                    min="0"
                    placeholder="e.g., 1"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              className="btn btn-sm"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button color="primary" className="btn btn-sm" type="submit">
              Update Zone
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this Zone? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            className="btn btn-sm"
            onClick={() => setShowDeleteModal(!showDeleteModal)}
          >
            Cancel
          </Button>
          <Button color="danger" className="btn btn-sm" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ZonesForCarrier
